import React, { useEffect, useState } from 'react';
import {
  Card,
  Typography,
  Button,
  Space,
  message,
  Tag,
  Table,
  Spin,
  Row,
  Col,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { Quotation } from '../services/quotations';
import customerApi from '../services/customerApi';
import { customerAuthService } from '../services/customerAuth';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

export const CustomerQuotationDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const [approving, setApproving] = useState(false);
  const customer = customerAuthService.getCurrentCustomer();

  useEffect(() => {
    if (id) {
      loadQuotation(parseInt(id, 10));
    }
  }, [id]);

  const loadQuotation = async (quotationId: number) => {
    setLoading(true);
    try {
      const response = await customerApi.get(`/customer/quotations/${quotationId}`);
      const loadedQuotation = response.data.data || response.data;
      setQuotation(loadedQuotation);
    } catch (error: any) {
      console.error('Failed to load quotation:', error);
      let errorMessage = 'Failed to load quotation';
      
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
        if (error.response.status === 401) {
          errorMessage = 'Unauthorized. Please login again.';
        } else if (error.response.status === 403) {
          errorMessage = 'Access denied.';
        } else if (error.response.status === 404) {
          errorMessage = 'Quotation not found.';
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      message.error(errorMessage);
      navigate('/customer/billing');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!quotation) return;
    
    setApproving(true);
    try {
      await customerApi.post(`/customer/quotations/${quotation.id}/approve`);
      message.success('Quotation approved successfully. Invoice has been created.');
      loadQuotation(quotation.id); // Reload quotation to get updated data
    } catch (error: any) {
      console.error('Failed to approve quotation:', error);
      let errorMessage = 'Failed to approve quotation';
      
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      message.error(errorMessage);
    } finally {
      setApproving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('DD/MM/YYYY');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!quotation) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Text>Quotation not found</Text>
      </div>
    );
  }

  const getQuotationDisplayName = () => {
    return quotation.quotation_number;
  };

  const quotationName = getQuotationDisplayName();

  const statuses: Array<'draft' | 'Pending' | 'Approved'> = ['draft', 'Pending', 'Approved'];
  const statusConfig = {
    draft: { bg: '#E6F7FF', border: '#40A9FF', color: '#1890ff', dot: '#1890ff', label: 'Draft' },
    Pending: { bg: '#FFF7E6', border: '#FFA940', color: '#FF8C00', dot: '#FF8C00', label: 'Pending' },
    Approved: { bg: '#F6FFED', border: '#73D13D', color: '#52C41A', dot: '#52C41A', label: 'Approved' },
  };
  const currentStatus = quotation.status || 'draft';

  // Get customer contact information
  const customerContact = quotation.customer?.contacts && quotation.customer.contacts.length > 0 
    ? quotation.customer.contacts[0] 
    : null;
  const addressParts: string[] = [];
  if (customerContact?.address1) addressParts.push(customerContact.address1);
  if (customerContact?.address2) addressParts.push(customerContact.address2);
  const addressText = addressParts.join('\n');
  const attention = quotation.customer?.name || '-';
  const contact = customerContact?.contact_number || customerContact?.email || '-';

  return (
    <div>
      {/* Header */}
      {quotationName && (
        <div style={{
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '1px solid #f0f0f0',
        }}>
          {/* First row: Title and buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
            paddingBottom: 12,
            borderBottom: '1px solid #f0f0f0',
          }}>
            <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
              {getQuotationDisplayName()}
            </Title>
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/customer/billing')}
              >
                Back
              </Button>
              {quotation.status === 'Pending' && (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={handleApprove}
                  loading={approving}
                >
                  Approve
                </Button>
              )}
            </Space>
          </div>
          {/* Second row: Status badges, aligned to the right */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}>
            <Space size={8}>
              {statuses.map((status) => {
                const isActive = currentStatus === status;
                const config = statusConfig[status];
                return (
                  <div
                    key={status}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      backgroundColor: isActive ? config.bg : '#f5f5f5',
                      border: isActive ? `1px solid ${config.border}` : '1px solid #d9d9d9',
                      color: isActive ? config.color : '#595959',
                      fontWeight: isActive ? 500 : 400,
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      transition: 'all 0.2s',
                    }}
                  >
                    <span style={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: isActive ? config.dot : '#8c8c8c',
                    }}></span>
                    {config.label}
                  </div>
                );
              })}
            </Space>
          </div>
        </div>
      )}

      {/* Content */}
      <Row gutter={24}>
        {/* Left Column */}
        <Col span={12}>
          <Card title="Customer Information" style={{ marginBottom: 16 }} bodyStyle={{ padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {/* Customer Type - 单独一行，合并后三列 */}
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Customer</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>Personal</div>
                  </td>
                </tr>
                {/* Customer - 单独一行，合并后三列 */}
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Customer</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{quotation.customer?.name || '-'}</div>
                  </td>
                </tr>
                {/* Address - 单独一行 */}
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Address</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div style={{ whiteSpace: 'pre-line' }}>{addressText || '-'}</div>
                  </td>
                </tr>
                {/* Email - 单独一行 */}
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Email</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{attention}</div>
                  </td>
                </tr>
                {/* Contact - 单独一行 */}
                <tr>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Contact</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{contact}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </Card>
        </Col>

        {/* Right Column */}
        <Col span={12}>
          <Card title="Quotation Details" style={{ marginBottom: 16 }} bodyStyle={{ padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {/* Quotation No. - 单独一行 */}
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Quotation No.</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{quotation.quotation_number}</div>
                  </td>
                </tr>
                {/* Invoice No. - 单独一行 */}
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Invoice No.</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{quotation.invoice?.invoice_number || '-'}</div>
                  </td>
                </tr>
                {/* Date - 单独一行 */}
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Date</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{formatDate(quotation.date)}</div>
                  </td>
                </tr>
                {/* Advisor - 单独一行 */}
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Advisor</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{quotation.agent?.name || '-'}</div>
                  </td>
                </tr>
                {/* Team - 单独一行 */}
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Team</strong>
                  </td>
                  <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                    <div>{quotation.team?.name || '-'}</div>
                  </td>
                </tr>
                {/* User - 单独一行 */}
                {quotation.staff && (
                  <tr>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>User</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      <div>{quotation.staff?.name || '-'}</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
        </Col>
      </Row>

      {/* Bottom Section */}
      <Card title="Task Information" style={{ marginBottom: 24 }} bodyStyle={{ padding: '16px' }}>
          <Table
            columns={[
              {
                title: 'Type',
                dataIndex: 'task_type_id',
                key: 'task_type_id',
                width: '15%',
                render: (_: any, record: any) => {
                  // Backend returns task_type (snake_case), not taskType (camelCase)
                  const taskType = record.task_type || record.taskType;
                  return <span>{taskType?.name || '-'}</span>;
                },
              },
              {
                title: 'Year',
                dataIndex: 'year',
                key: 'year',
                width: '10%',
                render: (year: number) => <span>{year || '-'}</span>,
              },
              {
                title: 'Task No.',
                dataIndex: 'task_template_id',
                key: 'task_no',
                width: '12%',
                render: (_: any, record: any) => {
                  // Backend returns task_template (snake_case), not taskTemplate (camelCase)
                  const taskTemplate = record.task_template || record.taskTemplate;
                  return <span>{taskTemplate ? `Task #${taskTemplate.id}` : '-'}</span>;
                },
              },
              {
                title: 'Task',
                dataIndex: 'task_template_id',
                key: 'task_template_id',
                width: '18%',
                render: (_: any, record: any) => {
                  // Backend returns task_template (snake_case), not taskTemplate (camelCase)
                  const taskTemplate = record.task_template || record.taskTemplate;
                  return <span>{taskTemplate?.name || '-'}</span>;
                },
              },
              {
                title: 'Unit',
                dataIndex: 'unit',
                key: 'unit',
                width: '10%',
                render: (unit: number | string) => {
                  const numValue = typeof unit === 'number' ? unit : parseFloat(unit || '0');
                  return <span>{!isNaN(numValue) ? numValue.toFixed(2) : '-'}</span>;
                },
              },
              {
                title: 'Price/Unit',
                dataIndex: 'price_per_unit',
                key: 'price_per_unit',
                width: '12%',
                render: (pricePerUnit: number) => {
                  const numValue = typeof pricePerUnit === 'number' ? pricePerUnit : parseFloat(pricePerUnit || '0');
                  return <span>{!isNaN(numValue) ? numValue.toFixed(2) : '0.00'}</span>;
                },
              },
              {
                title: 'Discount',
                dataIndex: 'discount',
                key: 'discount',
                width: '10%',
                render: (discount: number) => {
                  const numValue = typeof discount === 'number' ? discount : parseFloat(discount || '0');
                  return <span>{!isNaN(numValue) ? numValue.toFixed(2) : '0.00'}</span>;
                },
              },
              {
                title: 'Amount',
                dataIndex: 'amount',
                key: 'amount',
                width: '10%',
                render: (amount: number) => {
                  const numValue = typeof amount === 'number' ? amount : parseFloat(amount || '0');
                  return <span>{!isNaN(numValue) ? numValue.toFixed(2) : '0.00'}</span>;
                },
              },
            ]}
            dataSource={quotation.items?.map((item, index) => ({ ...item, key: index })) || []}
            pagination={false}
            size="small"
          />
        </Card>
    </div>
  );
};

